// Compact loans & advances context for the Overview: balances, progress and the
// next payroll deduction per obligation. The full workflow (apply, agreements,
// consent) stays in the Loans & Advances module — this section links, it never
// duplicates. Data: credit-service /me (source of truth for balances/schedules).
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Coins, ExternalLink } from 'lucide-react';
import { Badge } from '@/features/shared/components/ui/badge';
import { Button } from '@/features/shared/components/ui/button';
import { Skeleton } from '@/features/shared/components/ui/skeleton';
import type { CreditFacility, CreditOffer } from '@/features/credit/types';
import { facilityPill } from '@/features/credit/api/creditApi';
import { formatMoney, MASK } from '../lib/format';

interface LoansSummaryProps {
  facilities?: CreditFacility[];
  offers?: CreditOffer[];
  loading: boolean;
  error: boolean;
  revealed: boolean;
  onOpenLoans: () => void; // My Pay loans tab (pay context)
  onOpenModule: () => void; // full Loans & Advances module
}

export const OPEN_FACILITY_STATUSES = ['active', 'in_arrears', 'settling', 'pending_disbursement', 'disbursing'];

/** Employee-friendly funding label: employer vs partner product. */
export function fundingLabelKey(f: CreditFacility): string {
  return f.fundingModel === 'partner_funded' ? 'myPay.partnerProduct' : 'myPay.employerProduct';
}

export const productName = (f: CreditFacility, offers?: CreditOffer[]): string | null =>
  offers?.find((o) => o.productId === f.productId)?.name ?? null;

export const LoansSummary: React.FC<LoansSummaryProps> = ({
  facilities,
  offers,
  loading,
  error,
  revealed,
  onOpenLoans,
  onOpenModule,
}) => {
  const { t } = useTranslation('payroll');
  const open = (facilities ?? []).filter((f) => OPEN_FACILITY_STATUSES.includes(f.status));

  return (
    <section aria-labelledby="mypay-loans">
      <div className="mb-3 flex items-center justify-between gap-2">
        <h2 id="mypay-loans" className="flex items-center gap-2 text-h3 text-charcoal">
          <Coins className="h-5 w-5 text-[#D9711E]" /> {t('myPay.loansSectionTitle')}
        </h2>
        <Button variant="ghost" size="sm" className="gap-1.5" onClick={onOpenModule}>
          {t('myPay.openLoansModule')} <ExternalLink className="h-3.5 w-3.5" />
        </Button>
      </div>

      {loading ? (
        <Skeleton className="h-24 w-full rounded-xl" />
      ) : error ? (
        <p className="rounded-xl border border-border px-4 py-6 text-center text-sm text-muted-foreground">
          {t('myPay.loansUnavailable')}
        </p>
      ) : open.length === 0 ? (
        <div className="rounded-xl border border-border px-4 py-6 text-center">
          <p className="text-sm text-muted-foreground">{t('myPay.noActiveObligations')}</p>
          {(offers?.length ?? 0) > 0 && (
            <Button variant="link" size="sm" className="mt-1 text-[#D9711E]" onClick={onOpenModule}>
              {t('myPay.requestAdvance')}
            </Button>
          )}
        </div>
      ) : (
        <ul className="space-y-2">
          {open.map((f) => {
            const money = (n: number) => (revealed ? formatMoney(n, f.currency) : MASK);
            const repaid = Math.max(f.totalRepayable - f.outstandingBalance, 0);
            const progress = f.totalRepayable > 0 ? Math.min(repaid / f.totalRepayable, 1) : 0;
            const pill = facilityPill(f.status);
            return (
              <li key={f.id} className="rounded-xl border border-border p-4">
                <div className="flex flex-wrap items-center justify-between gap-2">
                  <span className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-charcoal">
                      {productName(f, offers) ?? t('myPay.genericLoan')}
                    </span>
                    <span className="text-[11px] text-muted-foreground">{t(fundingLabelKey(f))}</span>
                    <Badge variant={pill.variant as any} className="text-[10px]">{pill.label}</Badge>
                  </span>
                  <Button variant="link" size="sm" className="h-auto p-0 text-[#D9711E]" onClick={onOpenLoans}>
                    {t('myPay.viewSchedule')}
                  </Button>
                </div>
                <div className="mt-2 grid grid-cols-2 gap-2 text-sm sm:grid-cols-3">
                  <MiniStat label={t('myPay.outstanding')} value={money(f.outstandingBalance)} />
                  <MiniStat label={t('myPay.nextDeduction')} value={money(f.installment)} />
                  <MiniStat label={t('myPay.originalAmount')} value={money(f.principal)} />
                </div>
                <div className="mt-3">
                  <div
                    className="h-1.5 w-full overflow-hidden rounded-full bg-muted"
                    role="progressbar"
                    aria-valuenow={Math.round(progress * 100)}
                    aria-valuemin={0}
                    aria-valuemax={100}
                    aria-label={t('myPay.repaymentProgress')}
                  >
                    <div className="h-full rounded-full bg-[#F28A3A]" style={{ width: `${progress * 100}%` }} />
                  </div>
                  <p className="mt-1 text-[11px] text-muted-foreground">
                    {t('myPay.repaidOf', { repaid: money(repaid), total: money(f.totalRepayable) })}
                  </p>
                </div>
              </li>
            );
          })}
        </ul>
      )}
    </section>
  );
};

const MiniStat: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <div>
    <p className="text-[11px] text-muted-foreground">{label}</p>
    <p className="font-medium tabular-nums text-charcoal">{value}</p>
  </div>
);
